// Reads .xlsx workbooks (Excel, Monday.com "Export board to Excel", Google Sheets downloads, openpyxl) in the
// browser with no library: a ZIP directory walk, the platform's DecompressionStream("deflate-raw"), and a
// tolerant scan of the sheet XML. Date-formatted numbers come back as ISO dates ("2026-03-14").

export type Cell = string | number | boolean | null;
export interface Sheet {
  name: string;
  rows: Cell[][]; // row 0 is spreadsheet row 1; blank rows stay as [] so group gaps survive
}

interface ZipEntry {
  name: string;
  method: number;
  compressedSize: number;
  offset: number;
}

function u16(b: Uint8Array, o: number) {
  return b[o] | (b[o + 1] << 8);
}
function u32(b: Uint8Array, o: number) {
  return (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0;
}

function zipEntries(b: Uint8Array): Map<string, ZipEntry> {
  // End of central directory: signature 0x06054b50, searched backwards past an optional comment.
  let eocd = -1;
  for (let i = b.length - 22; i >= Math.max(0, b.length - 22 - 65536); i--) {
    if (u32(b, i) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error("This file isn't a valid .xlsx workbook.");
  const count = u16(b, eocd + 10);
  let p = u32(b, eocd + 16);
  const out = new Map<string, ZipEntry>();
  const dec = new TextDecoder();
  for (let i = 0; i < count; i++) {
    if (u32(b, p) !== 0x02014b50) break;
    const method = u16(b, p + 10);
    const compressedSize = u32(b, p + 20);
    const nameLen = u16(b, p + 28);
    const extraLen = u16(b, p + 30);
    const commentLen = u16(b, p + 32);
    const offset = u32(b, p + 42);
    const name = dec.decode(b.subarray(p + 46, p + 46 + nameLen));
    out.set(name, { name, method, compressedSize, offset });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return out;
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function readEntry(b: Uint8Array, e: ZipEntry): Promise<string> {
  const p = e.offset;
  if (u32(b, p) !== 0x04034b50) throw new Error("Damaged .xlsx file.");
  const start = p + 30 + u16(b, p + 26) + u16(b, p + 28);
  const raw = b.subarray(start, start + e.compressedSize);
  const bytes = e.method === 0 ? raw : e.method === 8 ? await inflateRaw(raw) : null;
  if (!bytes) throw new Error("This .xlsx uses a compression this reader doesn't know.");
  return new TextDecoder().decode(bytes);
}

const ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'" };
export function decodeXml(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, (_, ent: string) => {
    if (ent[0] === "#") {
      const code = ent[1] === "x" || ent[1] === "X" ? parseInt(ent.slice(2), 16) : parseInt(ent.slice(1), 10);
      return String.fromCodePoint(code);
    }
    return ENTITIES[ent.toLowerCase()] ?? "";
  });
}

function textOf(xml: string): string {
  // Phonetic runs (<rPh>) are reading aids, not part of the value.
  const clean = xml.replace(/<rPh\b[\s\S]*?<\/rPh>/g, "");
  let out = "";
  const re = /<t(?:\s[^>]*)?>([\s\S]*?)<\/t>|<t(?:\s[^>]*)?\/>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(clean))) out += m[1] ?? "";
  return decodeXml(out);
}

function colIndex(ref: string): number {
  let n = 0;
  for (const ch of ref) {
    const c = ch.charCodeAt(0);
    if (c < 65 || c > 90) break;
    n = n * 26 + (c - 64);
  }
  return n - 1;
}

const BUILTIN_DATE_FORMATS = new Set([14, 15, 16, 17, 18, 19, 20, 21, 22, 27, 30, 36, 45, 46, 47, 50, 57]);

function dateStyles(stylesXml: string | null): Set<number> {
  const dates = new Set<number>();
  if (!stylesXml) return dates;
  const custom = new Map<number, string>();
  for (const m of stylesXml.matchAll(/<numFmt\b[^>]*numFmtId="(\d+)"[^>]*formatCode="([^"]*)"/g)) {
    custom.set(+m[1], decodeXml(m[2]));
  }
  const xfs = /<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/.exec(stylesXml)?.[1] ?? "";
  let idx = 0;
  for (const m of xfs.matchAll(/<xf\b([^>]*)\/?>/g)) {
    const id = +(/numFmtId="(\d+)"/.exec(m[1])?.[1] ?? 0);
    const code = custom.get(id);
    // A custom format is a date when it has d, m or y outside quotes and brackets (not a plain number format).
    const isDate = BUILTIN_DATE_FORMATS.has(id) || (!!code && /[dmy]/i.test(code.replace(/"[^"]*"|\[[^\]]*\]/g, "")) && !/^[#0.,%\s]+$/.test(code));
    if (isDate) dates.add(idx);
    idx++;
  }
  return dates;
}

export function excelSerialToIso(serial: number): string {
  // Excel's 1900 date system, including its fictional 29 Feb 1900 (serial 60).
  const ms = Date.UTC(1899, 11, 30) + Math.round(serial * 86400000);
  return new Date(ms).toISOString().slice(0, 10);
}

function parseSheet(xml: string, shared: string[], dates: Set<number>): Cell[][] {
  const rows: Cell[][] = [];
  const rowRe = /<row\b([^>]*?)(?:\/>|>([\s\S]*?)<\/row>)/g;
  let rm: RegExpExecArray | null;
  let nextRow = 0;
  while ((rm = rowRe.exec(xml))) {
    const rAttr = /\br="(\d+)"/.exec(rm[1]);
    const r = rAttr ? +rAttr[1] - 1 : nextRow;
    nextRow = r + 1;
    const cells: Cell[] = [];
    const body = rm[2] ?? "";
    const cellRe = /<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g;
    let cm: RegExpExecArray | null;
    let nextCol = 0;
    while ((cm = cellRe.exec(body))) {
      const attrs = cm[1];
      const ref = /\br="([A-Z]+)\d+"/.exec(attrs)?.[1];
      const c = ref ? colIndex(ref) : nextCol;
      nextCol = c + 1;
      const t = /\bt="(\w+)"/.exec(attrs)?.[1] ?? "n";
      const s = +(/\bs="(\d+)"/.exec(attrs)?.[1] ?? -1);
      const inner = cm[2] ?? "";
      const v = /<v>([\s\S]*?)<\/v>/.exec(inner)?.[1];
      let value: Cell = null;
      if (t === "s") value = v != null ? shared[+v] ?? "" : null;
      else if (t === "inlineStr") value = textOf(inner);
      else if (t === "str") value = v != null ? decodeXml(v) : null;
      else if (t === "b") value = v === "1";
      else if (t === "e") value = null;
      else if (v != null && v !== "") {
        const num = Number(v);
        value = dates.has(s) && Number.isFinite(num) && num > 0 ? excelSerialToIso(num) : num;
      }
      while (cells.length < c) cells.push(null);
      cells[c] = value;
    }
    while (rows.length < r) rows.push([]);
    rows[r] = cells;
  }
  return rows;
}

/** Reads every sheet of a workbook. */
export async function readXlsx(buffer: ArrayBuffer | Uint8Array): Promise<Sheet[]> {
  const b = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const entries = zipEntries(b);
  const get = async (name: string) => {
    const e = entries.get(name);
    return e ? readEntry(b, e) : null;
  };
  const workbook = await get("xl/workbook.xml");
  if (!workbook) throw new Error("This file isn't an Excel workbook (.xlsx).");
  const rels = (await get("xl/_rels/workbook.xml.rels")) ?? "";
  const relTarget = new Map<string, string>();
  for (const m of rels.matchAll(/<Relationship\b([^>]*)\/?>/g)) {
    const id = /\bId="([^"]+)"/.exec(m[1])?.[1];
    const target = /\bTarget="([^"]+)"/.exec(m[1])?.[1];
    if (id && target) relTarget.set(id, target.startsWith("/") ? target.slice(1) : "xl/" + target.replace(/^\.\//, ""));
  }
  const sharedXml = await get("xl/sharedStrings.xml");
  const shared: string[] = [];
  if (sharedXml) for (const m of sharedXml.matchAll(/<si\b[^>]*>([\s\S]*?)<\/si>/g)) shared.push(textOf(m[1]));
  const dates = dateStyles(await get("xl/styles.xml"));
  const sheets: Sheet[] = [];
  let i = 0;
  for (const m of workbook.matchAll(/<sheet\b([^>]*)\/?>/g)) {
    i++;
    const name = decodeXml(/\bname="([^"]*)"/.exec(m[1])?.[1] ?? `Sheet${i}`);
    const rid = /\br:id="([^"]+)"/.exec(m[1])?.[1] ?? /\bid="([^"]+)"/.exec(m[1])?.[1];
    const path = (rid && relTarget.get(rid)) || `xl/worksheets/sheet${i}.xml`;
    const xml = await get(path);
    if (xml) sheets.push({ name, rows: parseSheet(xml, shared, dates) });
  }
  return sheets;
}
